import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import { parseUtcDate } from '@/lib/utcDate'

import type { FlatContentfulEntry } from '#contentful/ContentfulEntrySys'
import type { ContentfulPriceRuleSprite } from '#contentful/ContentfulPriceRuleSprite'

import { promotionsContent } from '@/content/promotions'

dayjs.extend(utc)
dayjs.extend(timezone)

/** Placeholder, in a price-rule sprite label, replaced by the rule's end date. Case-insensitive. */
const PRICE_RULE_END_DATE_PLACEHOLDER = /##endDate##/gi

// Price-rule end dates display in Eastern Time — matching the offer-window /
// session-day boundary (11:59 PM ET; see `SessionPhase`). Formatting in UTC would
// render the badge a day ahead, since 11:59 PM ET is early-morning the next day UTC.
const PRICE_RULE_DISPLAY_TIMEZONE = 'America/New_York'

/**
 * Formats a price rule's `calculatedEndDateUtc` as `MM/DD` for the `##endDate##` sprite placeholder.
 * The field is nullable; when no end date is available we fall back to the static string 'Soon'.
 * Formatted in Eastern Time (`PRICE_RULE_DISPLAY_TIMEZONE`) so the displayed day matches the offer
 * window's 11:59 PM ET boundary — formatting in UTC renders the badge a day ahead (11:59 PM ET is
 * early-morning the next day in UTC).
 */
export function formatPriceRuleEndDate(endDateUtc: Date | string | null | undefined): string {
  if (!endDateUtc) return promotionsContent.priceRuleEndDateFallback

  const date = dayjs(parseUtcDate(endDateUtc))
  if (!date.isValid()) return promotionsContent.priceRuleEndDateFallback

  return date.tz(PRICE_RULE_DISPLAY_TIMEZONE).format('MM/DD')
}

/**
 * Returns a price-rule sprite with the `##endDate##` placeholder in its `label` and `labelMobile`
 * replaced by the rule's end date (`MM/DD`), or 'soon' when none is set. The sprite is returned
 * untouched (same reference) when neither label uses the placeholder, to preserve render stability.
 */
export function withPriceRuleEndDate(
  sprite: FlatContentfulEntry<ContentfulPriceRuleSprite>,
  endDateUtc: Date | string | null | undefined
): FlatContentfulEntry<ContentfulPriceRuleSprite> {
  const usesPlaceholder =
    sprite.label.toLowerCase().includes('##enddate##') ||
    !!sprite.labelMobile?.toLowerCase().includes('##enddate##')
  if (!usesPlaceholder) return sprite

  const endDateText = formatPriceRuleEndDate(endDateUtc)
  return {
    ...sprite,
    label: sprite.label.replace(PRICE_RULE_END_DATE_PLACEHOLDER, endDateText),
    labelMobile: sprite.labelMobile?.replace(PRICE_RULE_END_DATE_PLACEHOLDER, endDateText)
  }
}
